<template>
  <div class="p-4">
    <incremental-search
      :autoFocus="true"
      ref="incrementalSearch"
      :data="searchResult"
      icon="SearchIcon"
      placeholder="英名、日本名、またはMMSI番号で検索"
      @input="onInput"
      @selected="selected"
      :remainList="true"
      :skip-local-suggestion-filter="true"
    >
      <template v-slot:group="{ group_name }">
        <p class="aisea-text-14-700-white">
          {{ getGroupName(group_name) }}
        </p>
      </template>

      <template v-slot:vessel="{ suggestion }">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <div class="img-container w-8 mr-3 flex">
              <img
                :src="getIcon(suggestion.categoryId)"
                :alt="suggestion.name"
                class="responsive"
              />
            </div>
            <div class="leading-none mt-1">
              <!--              <p class="mb-1 font-semibold">{{ suggestion.name_ja || suggestion.name }}</p>-->
              <p class="mb-1 aisea-text-14-700-white">
                {{
                  getVesselName({
                    nameJa: suggestion.name_ja,
                    name: suggestion.name
                  })
                }}
                <img v-if="getCountryCode(suggestion.id)" :src="getCountryCode(suggestion.id)" border="1" style="height:1rem;margin-left:1rem;" />
              </p>
              <small
                class="aisea-text-12-400-white"
                >IMO: {{ suggestion.imo || "---" }} MMSI:
                {{ suggestion.mmsi || "---" }}<br>
                目的地: {{ getDt(suggestion.id) }} ETA: {{ getEta(suggestion.id) }}
              </small>
            </div>
          </div>
        </div>
      </template>

      <!-- No Items Slot -->
      <template v-slot:noResult>
        <div class="flex items-center">
          <feather-icon icon="InfoIcon" svgClasses="h-5 w-5" class="mr-4" />
          <span>見つかりませんでした</span>
        </div>
      </template>
    </incremental-search>
  </div>
</template>

<script>
import IncrementalSearch from "@/components/incremental-search/incrementalSearch.vue";
import { VesselMixin } from "@/mixins";
import moment from "moment-timezone";
import Mapbox from "mapbox-gl";
import * as MapVesselUtil from "@/utils/MapVesselUtil";
import { DateUtil } from "@/utils";
import Vue from 'vue';
import { db } from "@/firebase/firebaseConfig.js";

import AisService from "@/services/aisService";

import { createNamespacedHelpers } from "vuex";
import { debounce } from "lodash";
const { mapGetters, mapState, mapActions } = createNamespacedHelpers("liveMap");

export default {
  name: "VesselSearch",
  components: {
    IncrementalSearch,
  },
  mixins: [VesselMixin],
  data() {
    return {
      expiredPopup: null,
      timerId: null,
      vesselsAppendixCacheMap: {},
      debouncedSearchQueryUpdate: null,
    };
  },
  created() {
    this.debouncedSearchQueryUpdate = debounce(this.searchQueryUpdate, 300);
  },
  props: {
    map: Object
  },
  computed: {
    ...mapState({
      searchResult: state => state.searchResult
    }),
    ...mapGetters(["getCountryAlpha2"]),
  },
  watch: {},
  methods: {
    ...mapActions(["searchVessels", "showVesselDetail", "jumpTo"]),
    getVesselName(payload) {
      if(payload.nameJa){
        return `${payload.name}(${payload.nameJa})`;
      }
      return `${payload.name}`;
    },
    getGroupName(groupName) {
      if (groupName === "vessel") {
        return "船舶";
      } else if (groupName === "files") {
        return "プロット";
      } else if (groupName === "pages") {
        return "港";
      } else {
        return "バース";
      }
    },
    getDt(suggestionId){
      return this.vesselsAppendixCacheMap[suggestionId] && this.vesselsAppendixCacheMap[suggestionId].dt 
          ? this.vesselsAppendixCacheMap[suggestionId].dt : '---';
    },
    getEta(suggestionId){
      return this.vesselsAppendixCacheMap[suggestionId] && this.vesselsAppendixCacheMap[suggestionId].eta && this.vesselsAppendixCacheMap[suggestionId].eta.seconds 
          ? moment(this.vesselsAppendixCacheMap[suggestionId].eta.seconds * 1000).local().format('YYYY年MM月DD日 HH:mm') : '---';
    },
    getCountryCode(suggestionId){
      if ( this.vesselsAppendixCacheMap[suggestionId] && this.vesselsAppendixCacheMap[suggestionId].cc ) {
        let countryAlpha2 = this.getCountryAlpha2(
          this.vesselsAppendixCacheMap[suggestionId].cc
        );
        return countryAlpha2 ? require(`@/assets/images/flags/${countryAlpha2}.svg`) : null;
      } else {
        return null;
      }
    },
    async searchQueryUpdate(query) {
      await this.searchVessels(query.trim());
      this.$refs.incrementalSearch.updateSuggestionsList();

      this.searchResult.vessel.data.forEach(async (vessel) => {
        // Dt,ETAをfirestoreから取得
        if (!this.vesselsAppendixCacheMap[vessel.id]) {
          const vesselInfoDoc = await db.doc(`vessel_info/${vessel.id}`).get();
          Vue.set(this.vesselsAppendixCacheMap, vessel.id, vesselInfoDoc.data());        
        }
      });
    },
    async selected(val) {
      await this.showVesselDetail(val.vessel.id);
      let position = await AisService.getDynamicAisInfo(val.vessel.id);
      let info = await AisService.getStaticInfo(val.vessel.id);
      if (position) {
        let coordinates = {
          center: [position.l.longitude, position.l.latitude]
        };
        await this.jumpTo(coordinates);
      }
      await this.showNotifyTimeout(val.vessel.id, position, info);
    },
    getIcon(id) {
      let iconName = this.getVesselCategoryProps(id).icon;
      return require(`@/assets/images/icons/vessels/${iconName}`);
    },
    clearTempLayers() {
      if (this.map.getLayer("TempSymbolLayer"))
        this.map.removeLayer("TempSymbolLayer");
      if (this.map.getLayer("TempPolygonLayer"))
        this.map.removeLayer("TempPolygonLayer");
      if (this.map.getLayer("TempTextLayer"))
        this.map.removeLayer("TempTextLayer");
      if (this.map.getLayer("TempWithoutLayer"))
        this.map.removeLayer("TempWithoutLayer");
    },
    displayTempLayers(featuresForPoint, featuresForPolygon) {
      // ソース生成
      if (typeof this.map.getSource("TempPointSource") == "undefined") {
        this.map.addSource("TempPointSource", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: []
          }
        });
      }
      this.map.getSource("TempPointSource").setData({
        type: "FeatureCollection",
        features: featuresForPoint
      });

      // Without 表示
      this.map.addLayer({
        id: "TempWithoutLayer",
        type: "symbol",
        source: "TempPointSource",
        minzoom: 14,
        maxzoom: 24,
        filter: ["all", ["==", ["number", ["get", "is_size"]], 0]],
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r"
          }
        },
        paint: {
          "icon-opacity": 0.5
        }
      });

      // Symbol 表示
      this.map.addLayer({
        id: "TempSymbolLayer",
        type: "symbol",
        source: "TempPointSource",
        minzoom: 10,
        maxzoom: 14,
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{i}",
          "icon-rotate": {
            type: "identity",
            property: "r"
          }
        },
        paint: {
          "icon-opacity": 0.5
        }
      });

      // Text 表示
      this.map.addLayer({
        id: "TempTextLayer",
        type: "symbol",
        source: "TempPointSource",
        layout: {
          "text-field": "{n}",
          "text-anchor": "top",
          "text-size": 14,
          "text-line-height": 1.2,
          "text-offset": [0, 1.1],
          "text-allow-overlap": true,
          "text-ignore-placement": true
        },
        paint: {
          "text-color": "#FFFFFF",
          "text-halo-color": "#000000",
          "text-halo-width": 1
        }
      });

      // Polygon 表示
      if (typeof this.map.getSource("TempPolygonSource") == "undefined") {
        this.map.addSource("TempPolygonSource", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: []
          }
        });
      }
      this.map.getSource("TempPolygonSource").setData({
        type: "FeatureCollection",
        features: featuresForPolygon
      });

      this.map.addLayer({
        id: "TempPolygonLayer",
        type: "fill",
        source: "TempPolygonSource",
        minzoom: 14,
        maxzoom: 24,
        paint: {
          "fill-color": {
            type: "identity",
            property: "co"
          }
        }
      });
    },
    async showNotifyTimeout(vesselId, position, info) {
      if (position && position.l.latitude && position.l.longitude) {
        const tenMinutesAgo = moment()
          .subtract(10, "minutes")
          .utc()
          .unix();
        const lastPosAt = moment(position.t).unix();

        // 最終計測時間が10分前の場合
        if (lastPosAt < tenMinutesAgo) {
          let featuresForPoint = [];
          let featuresForPolygon = [];

          let epochSecond = 0; // 透明度は固定なのでDammyを代入

          let feature = MapVesselUtil.createPointFeature(
            vesselId,
            position,
            info,
            epochSecond,
            false,
            false
          );
          featuresForPoint.push(feature);

          let featureForPolygonList = MapVesselUtil.createPolygonFeature(
            vesselId,
            position,
            info
          );
          featuresForPolygon = featuresForPolygon.concat(featureForPolygonList);

          if (this.expiredPopup !== null) {
            this.expiredPopup.remove();
            this.expiredPopup = null;
          }

          this.clearTempLayers();
          this.displayTempLayers(featuresForPoint, featuresForPolygon);

          const name = info.n_ja || info.n;
          const dateFormat = moment(position.t).format("YYYY/MM/DD HH:mm");
          let elapsed = DateUtil.elapsedTime(dateFormat);

          if (this.timerId) {
            clearTimeout(this.timerId);
            this.timerId = null;
          }

          this.expiredPopup = new Mapbox.Popup({
            closeOnClick: false,
            closeButton: true,
            offset: 15
          })
            .setLngLat([position.l.longitude, position.l.latitude])
            .setText(
              `${name}はこの位置で、${elapsed}(${dateFormat})に記録されました。`
            )
            .addTo(this.map);
          this.expiredPopup.addClassName("expired-vessel-live-position-popup");
          this.timerId = setTimeout(() => {
            this.expiredPopup.remove();
          }, 60000); // expiredPopupは1分経過で自動で消える

          this.expiredPopup.on("close", () => {
            this.expiredPopup = null;
            this.clearTempLayers();
          });
        }
      } else {
        this.$vs.notify({
          text: "位置情報が取得できません。",
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
          time: 10000
        });
      }
    },
    onInput(query) {
      this.debouncedSearchQueryUpdate(query);
    }
  },
  beforeDestroy() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }
};
</script>

<style lang="scss">
.expired-vessel-live-position-popup {
  max-width: 200px;
}

.expired-vessel-live-position-popup > .mapboxgl-popup-tip {
  border-top-color: #333333 !important;
  border-bottom-color: #333333 !important;
}

.expired-vessel-live-position-popup > .mapboxgl-popup-content {
  background-color: #333333 !important;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 21px;
  color: #f2f2f2;
  padding: 12px;
}

.expired-vessel-live-position-popup
  > .mapboxgl-popup-content
  > .mapboxgl-popup-close-button {
  color: #f2f2f2 !important;
  font-size: 20px;
  padding-right: 4px;
}
</style>
